import { CSSProperties, FC, useEffect } from 'react';
import Highlight from '@tiptap/extension-highlight';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import TextAlign from '@tiptap/extension-text-align';
import { Placeholder } from '@tiptap/extensions';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import RichTextToolbar from '../RichTextToolbar';

import './index.css';

interface RichTextFieldProps {
    disabled?: boolean;
    minHeight?: string | number;
    onBlur?: (value: string) => void;
    onChange: (value: string) => void;
    placeholder?: string;
    value: string;
}

const RichTextField: FC<RichTextFieldProps> = ({
    disabled,
    minHeight = '10rem',
    onBlur,
    onChange,
    placeholder = 'Add A Description...',
    value,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const editor = useEditor({
        content: value,
        immediatelyRender: false,
        editorProps: {
            attributes: {
                'aria-label': placeholder,
                class: 'rich-text-field__content',
            },
        },
        extensions: [
            StarterKit.configure({
                link: {
                    autolink: true,
                    linkOnPaste: true,
                    openOnClick: false,
                },
            }),
            Highlight,
            TaskList,
            TaskItem.configure({ nested: true }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Placeholder.configure({ placeholder }),
        ],
        onBlur: ({ editor: nextEditor }) => onBlur?.(nextEditor.getHTML()),
        onUpdate: ({ editor: nextEditor }) => onChange(nextEditor.getHTML()),
    });

    useEffect(() => {
        editor?.setEditable(!disabled);
    }, [disabled, editor]);

    useEffect(() => {
        if (!editor || editor.getHTML() === value) {
            return;
        }

        editor.commands.setContent(value || '', { emitUpdate: false });
    }, [editor, value]);

    return (
        <div
            className='rich-text-field'
            style={
                {
                    '--rich-text-border': token.colorBorder,
                    '--rich-text-placeholder': token.colorTextPlaceholder,
                    '--rich-text-secondary': token.colorTextSecondary,
                    background: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    opacity: disabled ? 0.65 : 1,
                } as CSSProperties
            }
        >
            {editor ? (
                <div style={{ borderBottom: `1px solid ${token.colorBorder}` }}>
                    <RichTextToolbar disabled={disabled} editor={editor} />
                </div>
            ) : null}
            <EditorContent editor={editor} style={{ minHeight }} />
        </div>
    );
};

export default RichTextField;
